// ISDP CUSTOM: gates the DevHub Self Service section. The user is already signed in to DevHub, so we do
// NOT make them authenticate again: on localhost we mint the session headlessly (dev-login); on deployed
// the BFF rides the existing Okta SSO session. Provides the user's role to children (read-only gating),
// blocks 'blocked' users, and surfaces a sign-out control.
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { DsoRoleContext, useDevSecOpsAuth, type DsoAuthState } from './useDevSecOpsAuth';
import { Spinner } from './dsoShared';
import './devsecops.css';

const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// E2E hook (localhost only): drive the REAL Okta/BFF login() instead of the headless devLogin(), so the
// suite exercises the genuine Self Service auth flow locally. Set via ?okta=1 or localStorage.
const forceOktaLocally = () => {
  if (typeof window === 'undefined') return false;
  try {
    return (
      new URLSearchParams(window.location.search).get('okta') === '1' ||
      window.localStorage.getItem('devhub-e2e-okta') === '1'
    );
  } catch {
    return false;
  }
};

/** Establish a DevSecOps session the same way the gate does: headless dev-login on localhost (unless
 *  the e2e Okta override is set), the real Okta/BFF login() redirect everywhere else. */
export const startDsoSession = (auth: Pick<DsoAuthState, 'login' | 'devLogin'>): Promise<void> =>
  isLocalhost && !forceOktaLocally() ? auth.devLogin() : auth.login();

export const DevSecOpsAuthGate = ({ children }: { children: ReactNode }) => {
  const { loading, authenticated, role, login, devLogin, logout } = useDevSecOpsAuth();

  // Single-login UX: when not yet authenticated to Self Service, establish the session automatically
  // rather than showing a second sign-in. On localhost that's the headless dev-login.
  // Auto-continue: establish the Self Service session automatically rather than showing a
  // second sign-in. Localhost mints headlessly (dev-login); deployed rides the existing Okta
  // SSO via login(). Guarded so a failed attempt falls back to the manual button (deployed).
  const tried = useRef(false);
  useEffect(() => {
    if (loading || authenticated || tried.current) return;
    tried.current = true;
    startDsoSession({ login, devLogin }).catch(() => { tried.current = false; });
  }, [loading, authenticated, devLogin, login]);

  // While loading, or while the auto-continue attempt is still in flight, show a spinner
  // splash rather than a second sign-in prompt.
  if (loading || (!authenticated && tried.current)) {
    return (
      <div className="dso">
        <div className="dso__body"><Spinner label="Connecting to Self Service…" /></div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="dso">
        <div className="dso__area">
          <div className="dso__area-titles">
            <span className="dso__eyebrow">Dräger DevHub</span>
            <h1 className="dso__title">DevHub Self Service</h1>
          </div>
        </div>
        <div className="dso__body">
          <div className="dso-panel" style={{ maxWidth: 520 }}>
            <h2 style={{ marginTop: 0 }}>Connecting…</h2>
            <p className="dso__muted" style={{ marginBottom: 18 }}>
              Self Service uses your existing Dräger DevHub identity — no separate sign-in. If it doesn't
              continue automatically, use the button below.
            </p>
            <odx-button type="button" variant="primary" onClick={() => { tried.current = false; login(); }}>
              <odx-icon slot="prefix" name="core::lock" size="sm" /> Continue with Dräger
            </odx-button>
          </div>
        </div>
      </div>
    );
  }

  if (role === 'blocked') {
    return (
      <div className="dso">
        <div className="dso__body">
          <div className="dso-panel" style={{ maxWidth: 560 }}>
            <div className="dso-panel__note">
              <odx-icon name="core::error-filled" size="md" />
              <span>Your account is <strong>blocked</strong> from the DevSecOps Hub. Contact the Hub admins for access.</span>
            </div>
            <div style={{ marginTop: 16 }}>
              <odx-button type="button" variant="secondary" onClick={() => logout()}>Sign out</odx-button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No second "tool-in-a-tool" top bar — Self Service is part of DevHub, you're already signed in.
  return <DsoRoleContext.Provider value={role}>{children}</DsoRoleContext.Provider>;
};
